import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";
import { Anchor, Building2, LifeBuoy, Users, Phone, Sparkles, Briefcase, Wallet, FileText } from "lucide-react";
import {
  ResizableNavbar,
  NavBody,
  MobileNav,
  MobileNavHeader,
  MobileNavMenu,
  MobileNavToggle,
  NavbarLogo,
  NavbarButton,
} from "../ui/ResizableNavbar";

const navLinks = [
  { name: "Home", link: "/", icon: <Anchor className="h-4 w-4" /> },
  { name: "About Us", link: "/about", icon: <Building2 className="h-4 w-4" /> },
  { name: "Why Choose Us", link: "/why-choose-us", icon: <Users className="h-4 w-4" /> },
  { name: "Services", link: "/services", icon: <LifeBuoy className="h-4 w-4" /> },
  { name: "Clients", link: "/clients", icon: <Briefcase className="h-4 w-4" /> },
  { name: "Tariff", link: "/tariff", icon: <Wallet className="h-4 w-4" /> },
  { name: "Contact Us", link: "/contact", icon: <Phone className="h-4 w-4" /> },
];

export function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();

  const handleMobileToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleMobileClose = () => {
    setIsOpen(false);
  };

  return (
    <ResizableNavbar className="fixed top-0 w-full z-50">
      {/* Desktop Navigation */}
      <NavBody>
        {({ visible }) => (
          <>
            <NavbarLogo 
              logoSrc="/assets/images/mainwebsiteLogo.png"
              logoAlt="Mega Marine Container Terminals Logo"
              companyName="Mega Marine"
              companyTagline="Container Terminals"
              href="/"
              visible={visible}
            />
            
            <div className="relative flex-1 flex items-center justify-center">
              <div className={cn(
                "flex items-center gap-1.5 p-2.5 rounded-xl transition-all duration-500",
                "bg-gray-50/95 backdrop-blur-xl border border-gray-100/50 shadow-sm"
              )}>
                {navLinks.map((link, idx) => (
                  <Link
                    key={link.name}
                    to={link.link}
                    className={cn(
                      "relative px-6 py-3 flex items-center gap-3 rounded-lg transition-all duration-300 font-medium whitespace-nowrap navbar-nav-link",
                      location.pathname === link.link
                        ? "bg-primary text-white shadow-md hover:bg-primary/90 hover:text-white"
                        : "text-gray-700 hover:text-white hover:bg-primary/80 hover:shadow-md hover:-translate-y-0.5",
                    )}
                    style={{ fontFamily: 'Poppins, var(--modern-font), Inter, Roboto, Nunito Sans, Arial, sans-serif' }}
                  >
                    {link.icon}
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <Link to="/contact">
              <NavbarButton 
                variant="primary"
                className="flex items-center gap-2 navbar-nav-btn"
              >
                <FileText className="h-4 w-4" />
                Get Quote
              </NavbarButton>
            </Link>
          </>
        )}
      </NavBody>

      {/* Mobile Navigation */}
      <MobileNav>
        {({ visible }) => (
          <>
            <MobileNavHeader>
              <NavbarLogo 
                logoSrc="/assets/images/mainwebsiteLogo.png"
                logoAlt="Mega Marine Container Terminals Logo"
                companyName="Mega Marine"
                companyTagline="Container Terminals"
                href="/"
                visible={visible}
              />
              <MobileNavToggle isOpen={isOpen} onClick={handleMobileToggle} visible={visible} />
            </MobileNavHeader>
            
            <MobileNavMenu isOpen={isOpen} onClose={handleMobileClose}>
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Link
                    to={link.link}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-all duration-300 w-full",
                      "hover:bg-white hover:shadow-md",
                      "hover:scale-[1.02] hover:-translate-y-0.5 group",
                      location.pathname === link.link
                        ? "bg-primary text-white shadow-md hover:bg-primary/90"
                        : "text-gray-700 hover:text-primary"
                    )}
                    onClick={handleMobileClose}
                  >
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 5 }}
                      className="flex-shrink-0"
                    >
                      {link.icon}
                    </motion.div>
                    {link.name}
                    {location.pathname === link.link && (
                      <motion.div
                        className="ml-auto"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Sparkles className="h-4 w-4" />
                      </motion.div>
                    )}
                  </Link>
                </motion.div>
              ))}
              
              <motion.div
                className="mt-4 w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: navLinks.length * 0.1 }}
              >
                <Link to="/contact">
                  <NavbarButton 
                    variant="primary"
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Get Quote
                  </NavbarButton>
                </Link>
              </motion.div>
            </MobileNavMenu>
          </>
        )}
      </MobileNav>

      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1.5 h-1.5 bg-primary/10 rounded-full blur-[1px]"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: 5 + i,
              repeat: Infinity,
              delay: i * 0.7,
              ease: "easeInOut",
            }}
            style={{
              left: `${20 + i * 15}%`,
              top: "50%",
            }}
          />
        ))}
      </div>
    </ResizableNavbar>
  );
}

